/**
 * Клиентская телеметрия (17.15).
 *
 * Шлём ровно два вида событий: какой экран открыли и что итог дня посмотрели.
 * Всё остальное пишет сервер в момент самого действия — так метрика совпадает
 * с реальностью и её нельзя накрутить из консоли.
 *
 * Персональных данных здесь нет и быть не может: в свойства уходят только
 * коды экранов. Сервер дополнительно фильтрует всё, что пришло.
 */

import { api } from '@/shared/api/client'

/** Имена, которые сервер принимает от клиента. Остальное он молча отбросит. */
export type ClientEvent = 'screen_view' | 'daily_summary_viewed' | 'onboarding_started'

type Props = Record<string, string | number | boolean>

interface QueuedEvent {
  name: ClientEvent
  props?: Props
}

// Идентификатор запуска приложения: позволяет отличить «один человек открыл
// пять экранов» от «пять человек открыли по одному». К личности не привязан.
const sessionId = Math.random().toString(36).slice(2, 14)

const FLUSH_DELAY_MS = 4000
const MAX_BATCH = 10

let queue: QueuedEvent[] = []
let timer: ReturnType<typeof setTimeout> | undefined

async function flush(): Promise<void> {
  if (queue.length === 0) return
  const events = queue.slice(0, MAX_BATCH)
  queue = queue.slice(MAX_BATCH)

  try {
    await api.post('/analytics', { session_id: sessionId, events }, { retries: 0 })
  } catch {
    // Аналитика никогда не мешает работе: не показываем ошибку и не копим
    // очередь бесконечно — потерянный screen_view ничего не стоит.
  }
}

/** Отправить событие. Вызов безопасен где угодно: он ничего не ждёт и не бросает. */
export function track(name: ClientEvent, props?: Props): void {
  queue.push(props ? { name, props } : { name })

  if (queue.length >= MAX_BATCH) {
    void flush()
    return
  }
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => void flush(), FLUSH_DELAY_MS)
}

/**
 * Досылать накопленное, когда приложение уходит из виду.
 *
 * В Telegram мини-апп часто закрывают свайпом, и без этого хвост событий
 * терялся бы у всех, кто не досидел до таймера.
 */
export function initAnalytics(): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush()
  })
}
